import {
  setHotelExpiredTimer,
  setHotelLoading,
} from "@/feature/hotel/checkout/state/hotel-data.slice";
import { HOTEL_DURATION_EXPIRED, HOTEL_NATIONALITY } from "@/feature/hotel/search/types/hotel-search.constant";
import { formatDateV1 } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import {
  clearAllHotelFilters,
  setHotelForExtractFilter,
} from "@/feature/hotel/search/state/extract-hotel-filter-slice";
import { baseApi } from "@/redux/api/base-api";
import { RootState } from "@/redux/store";
import { HotelSearchResponse } from "../types/hotel-search-page-type";
import { IHotelSearchQueryArgs } from "../types/hotel-search-type";

let lastSearchQueryArgStr: string | null = null;
const HOTEL_SEARCH_CACHE_SECONDS = Math.floor(HOTEL_DURATION_EXPIRED / 1000);

function parseHotelTimerValue(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function resolveHotelTimerExpiry(response: HotelSearchResponse) {
  const rawValue =
    parseHotelTimerValue(response.ttl) ??
    parseHotelTimerValue(response.data?.ttl);

  if (rawValue === null || rawValue <= 0) {
    return Date.now() + HOTEL_DURATION_EXPIRED;
  }

  // Absolute Unix timestamp in milliseconds.
  if (rawValue >= 1_000_000_000_000) {
    return rawValue;
  }

  // Absolute Unix timestamp in seconds.
  if (rawValue >= 1_000_000_000) {
    return rawValue * 1000;
  }

  // Duration already expressed in milliseconds.
  if (rawValue >= 100_000) {
    return Date.now() + rawValue;
  }

  // Duration expressed in seconds.
  return Date.now() + rawValue * 1000;
}

export const hotelApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    hotelSearch: builder.query<HotelSearchResponse, IHotelSearchQueryArgs>({
      query: (body) => {
        const formattedBody = {
          client_nationality: HOTEL_NATIONALITY,
          checkin: formatDateV1(body.date.from),
          checkout: formatDateV1(body.date.to),
          destination: body.location.type,
          code: body.location.code,
          name: body.location.name,
          rooms: body.rooms.map((room) => {
            const payload: {
              adults?: number;
              children_ages?: number[];
              no_of_infants?: number;
            } = {};

            if (room.adults > 0) payload.adults = room.adults;
            if (room.children_ages && room.children_ages.length > 0) {
              payload.children_ages = room.children_ages;
            }
            if (room.infants > 0) payload.no_of_infants = room.infants;

            return payload;
          }),
        };

        return {
          url: "/b2c/hotel/search",
          method: "POST",
          body: formattedBody,
        };
      },
      providesTags: ["HOTEL_SEARCH"],
      keepUnusedDataFor: HOTEL_SEARCH_CACHE_SECONDS,

      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        dispatch(setHotelLoading(true));

        const currentArgStr = JSON.stringify(arg);
        const isRefresh = lastSearchQueryArgStr === currentArgStr;
        lastSearchQueryArgStr = currentArgStr;
        const shouldResetFilters =
          !isRefresh ||
          (getState() as RootState).extractHotelFilter.resetOnNextSearch;
        const keepFilters = !shouldResetFilters;

        try {
          if (shouldResetFilters) {
            dispatch(clearAllHotelFilters());
          }
          const { data } = await queryFulfilled;
          const hotel_list = data?.data?.hotels;
          if (hotel_list?.length) {
            dispatch(
              setHotelForExtractFilter({
                hotels: hotel_list,
                keepFilters,
              }),
            );
          }
          dispatch(setHotelExpiredTimer(resolveHotelTimerExpiry(data)));
        } catch {
          // Handled by error state
        } finally {
          dispatch(setHotelLoading(false));
        }
      },
      async onCacheEntryAdded(
        arg,
        { dispatch, cacheDataLoaded, getCacheEntry }
      ) {
        // This ensures the timer triggers even if served from RTK Query cache
        try {
          await cacheDataLoaded;
          const entry = getCacheEntry();
          if (entry.status === "fulfilled") {
            const currentArgStr = JSON.stringify(arg);
            const isRefresh = lastSearchQueryArgStr === currentArgStr;
            lastSearchQueryArgStr = currentArgStr;
            const keepFilters = isRefresh;

            if (!keepFilters) {
              dispatch(clearAllHotelFilters());
            }

            dispatch(setHotelExpiredTimer(resolveHotelTimerExpiry(entry.data)));
            const hotel_list = entry.data?.data?.hotels;
            if (hotel_list?.length) {
              dispatch(
                setHotelForExtractFilter({
                  hotels: hotel_list,
                  keepFilters,
                }),
              );
            }
          }
        } catch {
          // no-op
        }
      },
    }),
  }),
});

export const { useHotelSearchQuery, useLazyHotelSearchQuery } = hotelApi;
